import { isObject, isUUID } from 'class-validator';
import { isNil, isString, has, upperFirst } from 'lodash';
import { DateTime } from 'luxon';

import type { ICustomField } from './custom-field';
import type { ICustomFormData } from './custom-forms';
import { INovaEntity } from './base';
import { absDiffDatesInMillis, LuxonDateTimeFormats, toDateTime, Weekday } from './chronon';
import { IDock } from './dock';
import { Maybe } from './globals';
import { ILoadType } from './loadtype';
import { t } from './i18n';
import { duration_hr, ITimeInterval } from './interval';
import { breakWordsAtCaps } from './js-helpers';
import { GlobalLimits } from './limits';
import { NovaDate, type DateLike } from './nova-date';
import { IHasOrgId } from './org';
import { AppointmentStatus, StatusTimeline } from './status';
import { utcNowAsDate } from './timezone';
import { millisecondsIn } from './units';
import { isInternalUser, isWarehouseUser, IUser } from './user';
import { IWarehouse } from './warehouse';

export const MAX_RECURRING_APPOINTMENT_WEEKS = GlobalLimits.MAX_RECURRING_APPOINTMENT_WEEKS.value;

// These two constants are applied for carrier users only, warehouse users does not have such limitations
export const ETA_WINDOW_HOURS = 24;
export const ETA_SET_ALLOWED_STATUSES = [AppointmentStatus.Scheduled];

export enum AppointmentType {
  Standard = 'Standard',
  Reserve = 'Reserve'
}

export const DEFAULT_RESERVE_DURATION_MIN = 30;

export type RecurringPattern = {
  numWeeks: number;
  weekDays: Weekday[];
};

export type RescheduleMetadata = {
  from: Date;
  to: Date;
  by: string;
  timestamp: Date;
};

export enum RecurringExtraFieldsToCopyEnum {
  refNumber = 'refNumber',
  customFields = 'customFields',
  notes = 'notes',
  tags = 'tags'
}

export enum EtaCondition {
  OnTime = 'OnTime',
  Late = 'Late',
  Early = 'Early'
}

export enum AppointmentMetadataEnum {
  clonedFromId = 'clonedFromId',
  externalValidationFailed = 'externalValidationFailed',
  externalValidationErrorMessage = 'externalValidationErrorMessage',
  unitLimitBreached = 'unitLimitBreached',
  unitLimitOverrideReason = 'unitLimitOverrideReason'
}

export type AppointmentMetadata = {
  [AppointmentMetadataEnum.clonedFromId]?: string;
  [AppointmentMetadataEnum.externalValidationFailed]?: boolean;
  [AppointmentMetadataEnum.externalValidationErrorMessage]?: string;
  [AppointmentMetadataEnum.unitLimitBreached]?: boolean;
  [AppointmentMetadataEnum.unitLimitOverrideReason]?: string;
};

/** Row shape when custom form answers are attached for reporting / export */
export type AppointmentCustomFormExportRow = {
  feature: string;
  value: string;
  type: string;
  label: string | number;
};

export function getEtaVerb(etaCondition: EtaCondition): string {
  return etaCondition === EtaCondition.Late ? 'running' : 'arriving';
}

export function getEtaCondition(
  start: Maybe<Date>,
  eta: Maybe<Date>,
  timezone: string
): EtaCondition {
  let etaCondition = EtaCondition.OnTime;
  if (start && eta) {
    const appointmentStart = toDateTime(start, { zone: timezone }).startOf('minute');
    const appointmentEta = toDateTime(eta, { zone: timezone }).startOf('minute');
    const diff = appointmentStart.diff(appointmentEta, 'minutes').minutes;
    etaCondition =
      diff === 0 ? EtaCondition.OnTime : diff < 0 ? EtaCondition.Late : EtaCondition.Early;
  }
  return etaCondition;
}

export function getReadableEtaStatus(etaCondition: EtaCondition): string {
  let etaStatus: string;
  if (etaCondition === EtaCondition.OnTime) {
    etaStatus = `${etaCondition}`;
  } else {
    etaStatus = `${getEtaVerb(etaCondition)} ${etaCondition}`;
  }

  return upperFirst(breakWordsAtCaps(etaStatus));
}

export const ETA_PREFIX_STRING = 'ETA:';
export const OVERLIMIT_TAG = 'Overlimit';

export interface IAppointment extends INovaEntity, IHasOrgId, ITimeInterval {
  eta?: Date;
  type: AppointmentType;
  status: AppointmentStatus;
  statusTimeline?: StatusTimeline;
  userId?: Maybe<string>; // Not required anymore since Jan 2026
  user?: IUser;
  loadTypeId?: string;
  loadType?: ILoadType;
  dockId: string;
  orgId?: string;
  refNumber?: string;
  refNumbers?: string[];
  notes?: Maybe<string>;
  ccEmails?: string[];
  customFields?: ICustomField[];
  /** Load-type custom form rows (customformdata); not persisted on the appointment row — transient on API payloads. */
  customFormsData?: ICustomFormData[];
  recurringParentId?: string;
  recurringPattern?: RecurringPattern;
  reschedules?: RescheduleMetadata[];
  confirmationNumber: number;
  muteNotifications?: boolean;
  isCheckedInByCarrier?: boolean;
  metadata?: AppointmentMetadata;
  searchableCustomFields?: Maybe<string>; // Used for optimized search
  units?: Maybe<number>;
  dock?: IDock;
  assetVisit?: { id?: string };
  customFormData?: AppointmentCustomFormExportRow[];
}

/**
 * Returns true if the eta is within the valid time window from start
 * @param start
 * @param eta
 */
export const ETACarrierValidations = {
  isEtaOnWindow,
  isStatusAllowedForETAUpdate
};

function isEtaOnWindow(start: Date, eta: Date): boolean {
  const etaStartDiffHours =
    absDiffDatesInMillis(new Date(start), new Date(eta)) / millisecondsIn.hour;
  return ETA_WINDOW_HOURS - etaStartDiffHours >= 0;
}

export function isWithin24HoursWindow(date: DateLike, timezone: string): boolean {
  // Get the current time in the specified timezone
  const now = DateTime.now().setZone(timezone).set({ millisecond: 0, second: 0 });

  const inputDate = new NovaDate(date)
    .toDateTime()
    .setZone(timezone)
    .set({ millisecond: 0, second: 0 });

  // Calculate the difference in hours between now and the input date
  const diff = now.diff(inputDate, 'hours').hours;

  // Check if the difference is within 24 hours
  return Math.abs(diff) <= 24;
}

function isStatusAllowedForETAUpdate(status: AppointmentStatus): boolean {
  return ETA_SET_ALLOWED_STATUSES.includes(status);
}

export function isAppointmentCancellation(body: Partial<IAppointment>): boolean {
  return Boolean(
    isObject(body) && isStatusChange(body) && body.status === AppointmentStatus.Cancelled
  );
}

export function isDockChanging(appt: IAppointment, dto: IAppointment): boolean {
  return isUUID(dto.dockId) && dto.dockId !== appt.dockId;
}

export function isStatusTimelineChange(body: Partial<IAppointment>): boolean {
  return Boolean(isObject(body) && Boolean(body.statusTimeline));
}

export function isStatusOnlyChange(appt: Partial<IAppointment>): boolean {
  const mandatoryAttr = 'status';
  const otherAcceptedAttrs = ['lastChangedBy', 'orgId'];

  if (!isObject(appt)) {
    return false;
  }

  if (!has(appt, mandatoryAttr)) {
    return false;
  }

  const bodyKeys = Object.keys(appt).filter(key => key !== mandatoryAttr);
  return bodyKeys.every(key => otherAcceptedAttrs.includes(key));
}

export function isStatusChange(body: Partial<IAppointment>): boolean {
  return Boolean(isObject(body) && Boolean(body.status));
}

export function isPastAppointment(appointment: IAppointment): boolean {
  if (isNil(appointment.end)) {
    const idPart = appointment.id ? `id=${String(appointment.id)}` : 'id=(unknown)';
    throw new Error(`Core: isPastAppointment(): IAppointment.end is required (${idPart})`);
  }
  const date = new NovaDate(appointment.end).toJSDate();
  return date.getTime() <= utcNowAsDate().getTime();
}

export function isRecurringAppointmentParent(appointment: IAppointment): boolean {
  return isRecurringAppointment(appointment) && appointment.recurringParentId === appointment.id;
}

export function isRecurringAppointmentChild(appointment: IAppointment): boolean {
  return isRecurringAppointment(appointment) && !isRecurringAppointmentParent(appointment);
}

export function isRecurringAppointment(appointment: IAppointment): boolean {
  return Boolean(appointment?.recurringParentId);
}

export function isReserve(appointment: IAppointment): boolean {
  return appointment.type === AppointmentType.Reserve;
}

export function isRequested(param: IAppointment | AppointmentStatus): boolean {
  if (isNil(param)) {
    throw Error('No parameter provided');
  }
  if (isString(param)) {
    return param === AppointmentStatus.Requested;
  }
  if (!has(param, 'status')) {
    throw Error('Object provided does not have a "status" property');
  }
  return param.status === AppointmentStatus.Requested;
}

export function isScheduled(param: IAppointment | AppointmentStatus): boolean {
  if (isNil(param)) {
    throw Error('No parameter provided');
  }
  if (isString(param)) {
    return param === AppointmentStatus.Scheduled;
  }
  if (!has(param, 'status')) {
    throw Error('Object provided does not have a "status" property');
  }
  return param.status === AppointmentStatus.Scheduled;
}

export function isInProgress(param: IAppointment | AppointmentStatus): boolean {
  if (isNil(param)) {
    throw Error('No parameter provided');
  }
  if (isString(param)) {
    return param === AppointmentStatus.InProgress;
  }
  if (!has(param, 'status')) {
    throw Error('Object provided does not have a "status" property');
  }
  return param.status === AppointmentStatus.InProgress;
}

export function getReschedulesCount(appointment: IAppointment): number {
  if (!appointment) {
    return 0;
  }
  // TODO: Remove this backwards compatibility with old reschedule tag system when it is no longer needed
  let tagCounter = 0;
  const rescheduledRegExp =
    /^Rescheduled - [0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z$/u;

  if (Array.isArray(appointment.tags)) {
    for (const tag of appointment.tags) {
      if (tag && tag.match(rescheduledRegExp)) {
        tagCounter++;
      }
    }
  }

  return (appointment.reschedules ? appointment.reschedules.length : 0) + tagCounter;
}

export function getReschedulesString(reschedulesCount: number): string {
  if (reschedulesCount === 1) {
    return t('core.appointment.messages.rescheduledOnce');
  }
  if (reschedulesCount === 2) {
    return t('core.appointment.messages.rescheduledTwice');
  }
  return t('core.appointment.messages.rescheduledCountTimes', { count: reschedulesCount });
}

export function getReadableRecurringPatternParts(
  { numWeeks, weekDays }: { numWeeks: number; weekDays: string[] },
  utcStart: DateLike,
  timezone: string
) {
  const endDate = toDateTime(utcStart, { zone: timezone }).plus({ weeks: numWeeks });
  return {
    weekDays: weekDays.map(day => day.slice(0, 3)).join(', '),
    endDate: endDate.toFormat(LuxonDateTimeFormats.ShortMonthDayYear),
    endTime: endDate.toFormat('h:mma').toLowerCase()
  };
}

export function getEtaTagString(condition: EtaCondition): string {
  return `${ETA_PREFIX_STRING}${condition}`;
}

export function getAppointmentStartStatus(
  appt: IAppointment,
  isLowerCase = false
): AppointmentStatus | string {
  const startStatus: AppointmentStatus = appt.statusTimeline?.[AppointmentStatus.Requested]
    ? AppointmentStatus.Requested
    : AppointmentStatus.Scheduled;
  if (isLowerCase) {
    return startStatus.toLowerCase();
  }
  return startStatus;
}

export function hasOnlyCCEmails(appt: Partial<IAppointment>): boolean {
  // The orgId and lastChangedBy are set by the crud guard
  const allowedKeys = ['ccEmails', 'lastChangedBy', 'orgId'];
  if (!appt) {
    return false;
  }
  const keys = Object.keys(appt);
  return Boolean(
    Array.isArray(appt.ccEmails) && keys.length === 3 && allowedKeys.every(c => keys.includes(c))
  );
}

export function findAppointmentWithNearestDate(appointments: IAppointment[]): IAppointment | null {
  if (appointments?.length > 0) {
    return appointments.reduce((previous, current) => {
      const now = DateTime.now();
      const prevDiff = Math.abs(toDateTime(previous.start).diff(now, 'milliseconds').milliseconds);
      const currentDiff = Math.abs(
        toDateTime(current.start).diff(now, 'milliseconds').milliseconds
      );
      return prevDiff < currentDiff ? previous : current;
    });
  }
  return null;
}

/*
 * When return is null, means that everything is fine, no rules were applied
 * When return is a string, means that a rule were enforced, and the string is the
 * Error Message
 */
export function validateCarrierLeadTimeRules(
  user: IUser,
  appointment: IAppointment,
  dock: IDock,
  warehouse: IWarehouse
): string | null {
  if (!user?.id) {
    return 'Appointment cannot be modified by unknown user';
  }

  if (isWarehouseUser(user) || isInternalUser(user)) {
    return null;
  }

  if (!dock?.id) {
    return 'Invalid dock object';
  }

  if (!warehouse?.id || !warehouse.timezone) {
    return 'Invalid warehouse object';
  }
  const now = DateTime.now().setZone(warehouse.timezone);

  // This means that we can have an *optional* custom min lead time for UPDATEs
  const leadTime = dock.minCarrierLeadTimeForUpdates_hr ?? 0;

  const appointmentStart = new NovaDate(appointment.start).setTz(warehouse.timezone).toDateTime();

  // Explicit decision: Carriers can't modify appointments in the past
  if (appointmentStart < now) {
    return 'Cannot modify past appointment';
  }

  // Construct an interval that starts "NOW" and ends at the appointment's START
  const nowToApptInterval = { start: now.toJSDate(), end: appointment.start };
  const hoursTillApptStart = duration_hr(nowToApptInterval);
  if (hoursTillApptStart < leadTime) {
    return `Appointment cannot be modified within ${leadTime} hours of its start time`;
  }

  return null;
}
